import { useState, useEffect } from "react";
import { api } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { fmtDT } from "../../../lib/constants";
import { toast } from "sonner";
import { Truck, CheckCircle, XCircle, Article, ClipboardText, Info as InfoIcon, Warning, Key, ArrowsClockwise } from "@phosphor-icons/react";
import { Info, Panel, friendlyError } from "./_shared";
import CameraEvidenceCapture from "../../../components/evidence/CameraEvidenceCapture";

function AwbRow({ shipmentId, awb, reading, onSave, onRemove, onRead, onCaptured }) {
  const [num, setNum] = useState(awb.awb_number || "");
  const [wt, setWt] = useState(awb.awb_chargeable_weight_kg ?? "");
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 rounded border border-[hsl(var(--border))]" data-testid={`awb-row-${awb.id}`}>
      <input className="input-field" placeholder="AWB number" value={num} onChange={(e) => setNum(e.target.value)} data-testid={`awb-number-input-${awb.id}`} />
      <input type="number" step="0.5" className="input-field" placeholder="Chargeable wt (kg)" value={wt} onChange={(e) => setWt(e.target.value)} data-testid={`awb-weight-input-${awb.id}`} />
      <div className="flex items-center gap-1.5">
        <CameraEvidenceCapture shipmentId={shipmentId} docType="AWB" awbId={awb.id} label="AWB photo" stage="ORIGIN"
          onCaptured={onCaptured} buttonClassName="btn-secondary text-xs px-2"
          buttonLabel={awb.has_evidence ? `Retake · v${awb.evidence_version}` : "Capture photo"} />
        <button className="btn-ghost text-xs" onClick={() => onRead(awb.id, (n, w) => { if (n) setNum(n); if (w !== "") setWt(w); })} disabled={!awb.has_evidence || reading} data-testid={`awb-read-btn-${awb.id}`}>
          {reading ? "Reading…" : "Read"}
        </button>
      </div>
      <div className="flex items-center gap-1.5 justify-end">
        {awb.complete ? <span className="status-pill status-success text-[10px]" data-testid={`awb-complete-${awb.id}`}>Complete</span>
                      : <span className="status-pill status-warning text-[10px]" data-testid={`awb-incomplete-${awb.id}`}>Incomplete</span>}
        <button className="btn-primary text-xs px-2" onClick={() => onSave(awb.id, num, wt)} data-testid={`awb-save-btn-${awb.id}`}>Save</button>
        <button className="btn-ghost text-xs text-[hsl(var(--danger))]" onClick={() => onRemove(awb.id)} data-testid={`awb-remove-btn-${awb.id}`}>Remove</button>
      </div>
    </div>
  );
}

export function OriginCaptainPanel({ d, reload, canReceipt, canAwb, canHandover }) {
  const [awbSet, setAwbSet] = useState({ finalized: false, awbs: [], aggregate_chargeable_weight_kg: null });
  const [reading, setReading] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [block, setBlock] = useState(null);
  const [handingOver, setHandingOver] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const s = d.shipment;
  const status = s.current_status;

  const loadAwbs = async () => {
    try { const r = await api.get(`/shipments/${s.id}/awbs`); setAwbSet(r.data); }
    catch { /* non-fatal */ }
  };

  useEffect(() => {
    api.get(`/shipments/${s.id}/source-vehicle`).then((r) => setVehicles(r.data?.vehicles || [])).catch(() => setVehicles([]));
    api.get(`/shipments/${s.id}/blocks`).then((r) => setBlock(r.data?.active || null)).catch(() => setBlock(null));
    loadAwbs();
  }, [s.id, status]);

  const blocked = !!block;

  // SOURCE_RECEIPT reconciliation is derived purely from package scans (no manual count).
  const expected = d.packages.length;
  const sourceScans = (d.scans || []).filter((x) => x.scan_context === "SOURCE_RECEIPT").length;
  const receiptComplete = expected > 0 && sourceScans >= expected;

  // SAL Authority-Letter camera is the first receipt evidence step.
  const salCaptured = (d.documents || []).some((x) => x.doc_type === "SAL_SOURCE_RECEIPT" && x.is_stamped_evidence);

  const awbFinalised = awbSet.finalized || !!s.awb_chargeable_weight_kg;
  // AWB actions: assigned Source CAP or SUPER_ADMIN only (backend `_assert_awb_actor`); not Source CTOP.
  // B3 (Founder-locked) — AWB drafting/editing stays available during a Shipment Block; only Finalise is withheld.
  const awbEditable = status === "REACHED_SOURCE_AIRPORT" && canAwb && !awbFinalised;

  const addAwb = async () => {
    try { await api.post(`/shipments/${s.id}/awbs`, {}); await loadAwbs(); }
    catch (e) { toast.error(e.displayMessage); }
  };
  const saveAwb = async (awbId, number, weight) => {
    try {
      await api.put(`/shipments/${s.id}/awbs/${awbId}`, { awb_number: number || null, awb_chargeable_weight_kg: weight === "" || weight == null ? null : weight });
      await loadAwbs(); toast.success("AWB saved");
    } catch (e) { toast.error(e.displayMessage); }
  };
  const removeAwb = async (awbId) => {
    try { await api.delete(`/shipments/${s.id}/awbs/${awbId}`); await loadAwbs(); toast.success("AWB removed"); }
    catch (e) { toast.error(e.displayMessage); }
  };
  const readAwb = async (awbId, setLocal) => {
    setReading(awbId);
    try {
      const r = await api.post(`/shipments/${s.id}/awbs/${awbId}/extract`, {});
      const data = r?.data || {};
      if (data.available) { setLocal(data.awb_number || "", data.chargeable_weight_kg ?? ""); toast.success("Gemini prefilled — verify before finalising"); }
      else toast("Gemini unavailable — enter AWB values manually");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast(code === "AWB_PHOTO_REQUIRED" ? "Capture this AWB's photo first." : "Gemini unavailable — enter manually");
    } finally { setReading(null); }
  };
  const finalizeAll = async () => {
    if (!window.confirm("Finalise ALL AWBs? This is irreversible: the aggregate weight is frozen and final repricing/DELTA are computed. AWBs can no longer be added, edited or removed.")) return;
    setFinalizing(true);
    try { await api.post(`/shipments/${s.id}/awb-finalize`, {}); toast.success("All AWBs finalised"); reload(); await loadAwbs(); }
    catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "AWB_SET_INCOMPLETE") toast.error(`Incomplete AWBs: ${(det.incomplete || []).map((x) => x.awb_number || "(no number)").join(", ")}`);
      else toast.error(e.displayMessage);
    } finally { setFinalizing(false); }
  };

  const confirmHandover = async () => {
    setHandingOver(true);
    try {
      await api.post(`/shipments/${s.id}/source-captain/airline-handover`, {});
      toast.success("Handover to airline confirmed");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setHandingOver(false); }
  };

  return (
    <Panel icon={<Truck size={16} weight="bold" />} title="SOURCE CAPTAIN — AIRPORT FLOW">
      <div className="space-y-4 text-sm">
        {/* Active Shipment Block banner — freezes Source receipt */}
        {blocked && (
          <div className="px-3 py-2 rounded bg-[hsl(var(--danger)/0.1)] border border-[hsl(var(--danger)/0.4)] text-[hsl(var(--danger))]" data-testid="source-block-banner">
            <div className="font-semibold flex items-center gap-1.5"><XCircle size={15} weight="fill" /> Shipment Block active</div>
            <div className="text-xs mt-0.5">{block.reason}</div>
          </div>
        )}

        {/* Assigned Source vehicle — read-only (compare physically; corrections happen outside receipt) */}
        <div>
          <div className="overline mb-1">ASSIGNED SOURCE VEHICLES</div>
          {vehicles.length ? (
            <div className="space-y-1" data-testid="source-vehicle-readonly">
              {vehicles.map((v, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs border-b border-stone-100 pb-1">
                  <Info label="VEHICLE #">{v.vehicle_number || "—"}</Info>
                  <Info label="DRIVER">{v.driver_name || "—"}</Info>
                  <Info label="DRIVER MOBILE">{v.driver_mobile || "—"}</Info>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-[hsl(var(--muted))]" data-testid="source-vehicle-none">No source vehicle assigned yet.</span>
          )}
        </div>

        {/* Stage 1: Source-airport receipt — SAL camera first, then per-package SOURCE_RECEIPT scans */}
        <div>
          <div className="overline mb-1">1. SOURCE-AIRPORT RECEIPT</div>
          {s.source_airport_arrived_at ? (
            <div data-testid="source-receipt-complete">
              <span className="status-pill status-success">RECEIVED</span>{" "}
              <span className="text-[hsl(var(--muted))]">{fmtDT(s.source_airport_arrived_at)} · {sourceScans}/{expected} scanned</span>
            </div>
          ) : status === "SOURCE_VEHICLE_ASSIGNED" && canReceipt ? (
            <div className="space-y-3">
              {blocked ? (
                <span className="text-xs text-[hsl(var(--muted))]" data-testid="source-receipt-blocked">Receipt is frozen while a Shipment Block is active.</span>
              ) : (
                <>
                  <div>
                    <CameraEvidenceCapture
                      shipmentId={s.id}
                      docType="SAL_SOURCE_RECEIPT"
                      label="Authority Letter at source airport"
                      stage="SOURCE_RECEIPT"
                      onCaptured={reload}
                      buttonClassName="btn-secondary inline-flex items-center gap-1.5 text-sm"
                      buttonLabel={salCaptured ? "Retake Authority Letter photo" : "Capture Authority Letter photo"}
                    />
                    {salCaptured && (
                      <span className="ml-2 text-[11px] text-[hsl(var(--success))]" data-testid="sal-photo-status">Authority Letter photo captured</span>
                    )}
                  </div>
                  <div className="grid-card p-3">
                    <div className="overline">SCAN PROGRESS · SOURCE RECEIPT</div>
                    <div className={`heading text-2xl mono mt-1 ${receiptComplete ? "text-[hsl(var(--success))]" : ""}`} data-testid="source-scan-progress">
                      {sourceScans}/{expected}
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted))] mt-1">
                      {salCaptured
                        ? "Scan each package (QR) to reconcile receipt. The final scan completes receipt automatically — there is no manual count or Complete Receipt step."
                        : "Capture the Authority Letter photo first; package scanning unlocks after it."}
                    </p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <span className="text-xs text-[hsl(var(--muted))]" data-testid="source-receipt-waiting">Awaiting source vehicle assignment.</span>
          )}
        </div>

        {/* Stage 2: AWB finalisation — #3 multiple AWBs per shipment */}
        <div>
          <div className="overline mb-1">2. AWBs (IndiGo)</div>
          {awbFinalised ? (
            <div className="text-xs space-y-1" data-testid="awb-finalised-status">
              <div><span className="status-pill status-success">AWBs FINALISED</span> <span className="text-[hsl(var(--muted))]">Total {awbSet.aggregate_chargeable_weight_kg ?? s.awb_chargeable_weight_kg} kg</span></div>
              {(awbSet.awbs || []).map((a) => (
                <div key={a.id} className="text-[hsl(var(--muted))]" data-testid={`awb-final-row-${a.id}`}>AWB {a.awb_number} · {a.awb_chargeable_weight_kg} kg</div>
              ))}
            </div>
          ) : awbEditable ? (
            <div className="space-y-2" data-testid="awb-editor">
              {(awbSet.awbs || []).map((a) => (
                <AwbRow key={a.id} shipmentId={s.id} awb={a} reading={reading === a.id}
                        onSave={saveAwb} onRemove={removeAwb} onRead={readAwb} onCaptured={loadAwbs} />
              ))}
              {(awbSet.awbs || []).length === 0 && (
                <div className="text-xs text-[hsl(var(--muted))]" data-testid="awb-none">No AWBs yet. Add each airline AWB with its number, chargeable weight and its own photo.</div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button className="btn-secondary text-sm" onClick={addAwb} data-testid="awb-add-btn">+ Add AWB</button>
                <button className="btn-primary text-sm" onClick={finalizeAll} disabled={blocked || finalizing || (awbSet.awbs || []).length === 0} data-testid="awb-finalize-all-btn">
                  {finalizing ? "Finalising…" : "Finalise All AWBs"}
                </button>
              </div>
              {blocked && (
                <div className="text-xs text-[hsl(var(--danger))]" data-testid="awb-finalize-blocked-hint">Shipment blocked. Resolve the Block before finalising AWBs.</div>
              )}
              <div className="text-[10px] text-[hsl(var(--muted))]">Each AWB needs a number, a chargeable weight (rounded up to 0.5 kg) and its own accepted camera photo. Finalise All AWBs is one-time and irreversible; the total chargeable weight is the sum of all AWBs.</div>
            </div>
          ) : (
            <span className="text-xs text-[hsl(var(--muted))]" data-testid="awb-blocked">Blocked — needs REACHED_SOURCE_AIRPORT.</span>
          )}
        </div>

        {/* Stage 3: airline handover — one-click, assigned Source CAP only, no payload */}
        <div>
          <div className="overline mb-1">3. AIRLINE HANDOVER</div>
          {s.airline_handover_at ? (
            <div data-testid="handover-done"><span className="status-pill status-success">HANDED OVER</span> <span className="text-[hsl(var(--muted))]">{fmtDT(s.airline_handover_at)}</span></div>
          ) : canHandover && status === "REACHED_SOURCE_AIRPORT" ? (
            <div className="space-y-2">
              <button
                className="btn-primary w-full"
                onClick={confirmHandover}
                disabled={handingOver || blocked || !awbFinalised}
                data-testid="confirm-handover-to-airline"
              >
                {handingOver ? "Confirming…" : "Confirm Handover to Airline"}
              </button>
              <div className="text-[10px] text-[hsl(var(--muted))]" data-testid="handover-hint">
                {blocked
                  ? "Resolve the active Shipment Block first."
                  : !awbFinalised
                    ? "Finalise the AWB and settle any positive balance (DELTA) first."
                    : "One-click confirmation — no airline/flight/AWB entry. This is irreversible."}
              </div>
            </div>
          ) : status === "REACHED_SOURCE_AIRPORT" ? (
            <span className="text-xs text-[hsl(var(--muted))]" data-testid="handover-cap-only">Only the assigned Source Captain can confirm airline handover.</span>
          ) : (
            <span className="text-xs text-[hsl(var(--muted))]" data-testid="handover-waiting">Available after source-airport receipt and AWB finalisation.</span>
          )}
        </div>
      </div>
    </Panel>
  );
}

export function ReceiverPanel({ d }) {
  const { profile } = useAuth();
  const isAdmin = profile?.role === "SUPER_ADMIN" || profile?.role === "CTOP";

  return (
    <Panel icon={<Article size={16} weight="bold" />} title="RECEIVER">
      {d.receiver ? (
        <div className="text-sm space-y-1">
          <div className="id-chip text-xs">{d.receiver.public_receiver_id}</div>
          <div><b>{d.receiver.name}</b>{d.receiver.company ? ` · ${d.receiver.company}` : ""}</div>
          <div className="text-xs text-[hsl(var(--muted))]">{d.receiver.mobile} · {d.receiver.email}</div>
          {d.receiver.delivery_address && (
            <div className="text-xs mt-2">
              {d.receiver.delivery_address.line1}, {d.receiver.delivery_address.city}, {d.receiver.delivery_address.state} - {d.receiver.delivery_address.pincode}
            </div>
          )}
          {isAdmin && (
            <div className="mt-3 pt-3 border-t" data-testid="receiver-tracking-info-section">
              <div className="overline mb-1">RECEIVER TRACKING</div>
              <div className="text-[10px] text-[hsl(var(--muted))]" data-testid="receiver-tracking-guidance">
                The receiver uses <span className="mono">/track-my-shipment</span> with their shipment code and
                tracking password (sent in the booking email). Receiver Readiness and Goods Received are confirmed
                there via a one-time OTP sent to the receiver's email — there is no link to issue or share.
              </div>
            </div>
          )}
        </div>
      ) : <p className="text-sm text-[hsl(var(--muted))]">No receiver</p>}
    </Panel>
  );
}

export function OutForDeliveryPanel({ d, reload }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/out-for-delivery`, { note: note || null });
      toast.success("Shipment is OUT_FOR_DELIVERY");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };

  return (
    <Panel icon={<Truck size={16} weight="bold" />} title="MARK OUT FOR DELIVERY">
      <div data-testid="out-for-delivery-panel" className="space-y-3 text-sm">
        <p className="text-xs text-[hsl(var(--muted))]">
          Requires the current destination vehicle (Vehicle Number + Driver Mobile) and a
          VEHICLE_LOADED photo for that vehicle. No dispatch greenlight, no manual count.
        </p>
        <label className="block">
          <div className="overline mb-1">OPERATOR NOTE (optional)</div>
          <input className="input-field" value={note}
                 onChange={(e) => setNote(e.target.value)} data-testid="ofd-note" />
        </label>
        <button className="btn-primary" disabled={busy} onClick={submit} data-testid="ofd-submit">
          Mark Out For Delivery
        </button>
      </div>
    </Panel>
  );
}


// M9B U6 — Delivery OTP panel: authorised Resend (same code, 60s cooldown, resets attempts) at
// RECEIVER_READY_CONFIRMED / DESTINATION_VEHICLE_ASSIGNED / OUT_FOR_DELIVERY, and staff-assisted
// Goods Received (OTP entry) at OUT_FOR_DELIVERY. Actors: assigned Destination CTOP / CTRL / SA.
export function DeliveryOtpPanel({ d, reload }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(Date.now());
  const status = d.shipment.current_status;
  const canConfirm = status === "OUT_FOR_DELIVERY";
  const resendAt = d.shipment.delivery?.resend_available_at
    ? new Date(d.shipment.delivery.resend_available_at).getTime() : 0;
  const cooldownLeft = Math.max(0, Math.ceil((resendAt - now) / 1000));

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cooldownLeft]);

  const confirmDelivery = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/confirm-delivery`, { otp: otp.trim() });
      toast.success("Delivery confirmed");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/delivery-otp/resend`, {});
      toast.success("Delivery OTP resent to the receiver");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setResending(false); }
  };

  return (
    <Panel icon={<CheckCircle size={16} weight="bold" />} title="DELIVERY OTP">
      <div data-testid="delivery-otp-panel" className="space-y-3 text-sm">
        <p className="text-[hsl(var(--muted))]">
          The delivery code was emailed to the Receiver at Receiver Ready. The receiver can confirm
          Goods Received themselves on the public tracking page, or read the code back so you can
          confirm here{d.receiver?.mobile ? <> on <span className="mono font-medium" data-testid="dotp-receiver-mobile">+91 {d.receiver.mobile}</span></> : null}.
        </p>

        <div className="flex items-center gap-2">
          <button className="btn-secondary" disabled={resending || cooldownLeft > 0}
                  onClick={resend} data-testid="resend-delivery-otp-btn">
            <ArrowsClockwise size={14} weight="bold" className="inline mr-1" />
            {cooldownLeft > 0 ? `Resend in ${cooldownLeft}s` : "Resend Delivery OTP"}
          </button>
        </div>

        {canConfirm && (
          <>
            <input className="input-field mono tracking-widest" inputMode="numeric" maxLength={6}
                   placeholder="6-digit OTP" value={otp}
                   onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   data-testid="dotp-input" />
            <button className="btn-primary" disabled={busy || otp.length < 4} onClick={confirmDelivery} data-testid="dotp-confirm">
              Confirm Delivery
            </button>
          </>
        )}
      </div>
    </Panel>
  );
}


// M9B U6 — Force Delivered: dedicated SUPER_ADMIN-only terminal exception at OUT_FOR_DELIVERY. NO OTP.
// Visible to legitimate internal viewers; executable only by SUPER_ADMIN. Mandatory override reason.
export function ForceDeliveredPanel({ d, reload, canExecute }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/force-delivered`, { reason: reason.trim() });
      toast.success("Shipment force-marked DELIVERED");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };

  return (
    <Panel icon={<Warning size={16} weight="bold" />} title="FORCE DELIVERED (OVERRIDE)">
      <div data-testid="force-delivered-panel" className="space-y-3 text-sm">
        <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--warning,38_92%_50%))] bg-[hsl(var(--warning,38_92%_50%))/0.08] px-3 py-2">
          <Warning size={16} weight="fill" className="mt-0.5 text-[hsl(var(--warning,38_92%_50%))]" />
          <p className="text-xs">
            This bypasses the Receiver OTP confirmation and marks the shipment DELIVERED. It is
            <span className="font-semibold"> irreversible</span>. Use only when the receiver genuinely
            has the goods but cannot complete OTP confirmation.
          </p>
        </div>
        {canExecute ? (
          <>
            <label className="block">
              <div className="overline mb-1">REASON FOR OVERRIDE (required)</div>
              <textarea className="input-field" rows={2} value={reason} minLength={6}
                        onChange={(e) => setReason(e.target.value)} data-testid="force-delivered-reason"
                        placeholder="Explain why delivery is being forced" />
            </label>
            <button className="btn-primary" disabled={busy || reason.trim().length < 6}
                    onClick={submit} data-testid="force-delivered-submit">
              Force Delivered
            </button>
          </>
        ) : (
          <p className="text-xs text-[hsl(var(--muted))]" data-testid="force-delivered-sa-only">
            Only a Super Admin can force delivery.
          </p>
        )}
      </div>
    </Panel>
  );
}


// M9B U6 — Resend Tracking Credentials: SUPER_ADMIN-only resend of the SAME code + password to
// Sender + Receiver. Available through the lifecycle and up to 7 days after delivery.
export function TrackingCredentialsPanel({ d, reload }) {
  const [busy, setBusy] = useState(false);
  const available = !!d.shipment.delivery?.tracking_credentials_resend_available;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/shipments/${d.shipment.id}/resend-tracking-credentials`, {});
      toast.success(`Tracking credentials resent (${(r.data?.resent_targets || []).length} recipient(s))`);
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };

  if (!available) return null;
  return (
    <Panel icon={<Key size={16} weight="bold" />} title="RESEND TRACKING CREDENTIALS">
      <div data-testid="tracking-credentials-panel" className="space-y-3 text-sm">
        <p className="text-[hsl(var(--muted))]">
          Resend the existing shipment code and tracking password to the Sender and Receiver. The
          password is not changed and is never shown here.
        </p>
        <button className="btn-secondary" disabled={busy} onClick={submit} data-testid="resend-tracking-credentials-btn">
          Resend Tracking Credentials
        </button>
      </div>
    </Panel>
  );
}


// Module 7 Unit 2A — Destination CTOP manually marks Receiver Ready after a phone call to the
// shipment-specific Receiver.mobile. No receiver OTP/self-service, no readiness evidence.
export function MarkReceiverReadyPanel({ d, reload }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/shipments/${d.shipment.id}/mark-receiver-ready`, { note: note || null });
      toast.success("Receiver marked ready");
      reload();
    } catch (e) { toast.error(e.displayMessage); }
    finally { setBusy(false); }
  };
  return (
    <Panel icon={<CheckCircle size={16} weight="bold" />} title="MARK RECEIVER READY">
      <div data-testid="mark-receiver-ready-panel" className="space-y-3 text-sm">
        <p className="text-[hsl(var(--muted))]">
          Call the receiver on <span className="mono font-medium" data-testid="mrr-receiver-mobile">{d.receiver?.mobile ? `+91 ${d.receiver.mobile}` : "—"}</span> to
          confirm they are ready to receive, then mark readiness here.
        </p>
        <label className="block">
          <div className="overline mb-1">OPERATOR NOTE (optional)</div>
          <input className="input-field" value={note} onChange={(e) => setNote(e.target.value)} data-testid="mrr-note" />
        </label>
        <button className="btn-primary" disabled={busy} onClick={submit} data-testid="mrr-submit">
          Mark Receiver Ready
        </button>
      </div>
    </Panel>
  );
}



// M5 — Confirm Shipment and Authorise Pickup + confirmation record download / recovery retry.
// Owning Sender: final review + one confirm action + download. SUPER_ADMIN / assigned Source CTOP:
// minimal failure state + retry. Backend is authoritative; email never affects readiness.
export function ShipmentConfirmationPanel({ d, reload }) {
  const s = d.shipment;
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadState = async () => {
    try {
      const { data } = await api.get(`/booking/${s.id}/confirmation/state`);
      setState(data);
      if (data.is_owner && data.ready && !data.confirmed) {
        try { const p = await api.get(`/booking/${s.id}/confirmation/preview`); setPreview(p.data); } catch (e) {}
      }
    } catch (e) { setState(null); } finally { setLoading(false); }
  };
  useEffect(() => { loadState(); }, [s.id]); // eslint-disable-line

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/booking/${s.id}/confirm`);
      toast.success("Shipment confirmed and pickup authorised");
      await loadState(); reload && reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && detail.message) || friendlyError(e) || "Could not confirm. Please try again.");
      await loadState();
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try { await api.post(`/booking/${s.id}/confirmation/retry`); toast.success("Record generation retried"); await loadState(); reload && reload(); }
    catch (e) { toast.error(friendlyError(e) || "Retry failed. Please try again."); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/booking/${s.id}/confirmation/document`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open the document. Please try again."); }
  };

  if (loading || !state) return null;
  const { prerequisites: pr, ready, confirmed, pdf_status, download_available, is_owner, can_retry } = state;

  // Non-owner privileged view: only a minimal failure/retry surface when generation FAILED.
  if (!is_owner) {
    if (confirmed && pdf_status !== "STORED" && can_retry) {
      return (
        <Panel icon={<Article size={16} />} title="Shipment confirmation record">
          <p className="text-sm text-[hsl(var(--muted))] mb-3" data-testid="m5-admin-pending">
            The sender has confirmed, but the confirmation record is not yet generated. You may retry generation.
          </p>
          <button className="btn-primary" disabled={busy} onClick={retry} data-testid="m5-retry">Retry record generation</button>
        </Panel>
      );
    }
    if (confirmed && download_available && can_retry) {
      return (
        <Panel icon={<Article size={16} />} title="Shipment confirmation record">
          <button className="btn-ghost text-sm" onClick={download} data-testid="m5-admin-download">Download confirmation record</button>
        </Panel>
      );
    }
    // D-10 — read-only record view for an authorised Controller (backend may_access_record); no retry.
    if (confirmed && download_available && !can_retry) {
      return (
        <Panel icon={<Article size={16} />} title="Shipment confirmation record">
          {state.confirmed_at && <div className="overline text-[10px] mb-2" data-testid="m5-readonly-confirmed-at">Confirmed {fmtDT(state.confirmed_at)}</div>}
          <button className="btn-ghost text-sm" onClick={download} data-testid="m5-readonly-download">View confirmation record</button>
        </Panel>
      );
    }
    return null;
  }

  // Owning Sender views
  return (
    <Panel icon={<Article size={16} />} title="Confirm Shipment & Authorise Pickup">
      {confirmed ? (
        download_available ? (
          <div className="space-y-3" data-testid="m5-confirmed-complete">
            <div className="flex items-center gap-2 text-sm"><CheckCircle size={18} weight="fill" className="text-green-600" /> Shipment confirmed · pickup authorised</div>
            {state.confirmed_at && <div className="overline text-[10px]">Confirmed {fmtDT(state.confirmed_at)}</div>}
            <button className="btn-ghost text-sm" onClick={download} data-testid="m5-download-confirmation">Download Shipment Confirmation & Commercial Record</button>
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="m5-pending-notice">
            <div className="flex items-center gap-2"><InfoIcon size={18} className="text-[hsl(var(--muted))]" /> Your confirmation has been recorded.</div>
            <p className="text-[hsl(var(--muted))]">Air12 is completing your Shipment Confirmation record. Pickup will be authorised shortly — you don’t need to confirm again.</p>
          </div>
        )
      ) : !ready ? (
        <div className="space-y-2 text-sm" data-testid="m5-blocked">
          <p className="text-[hsl(var(--muted))]">Complete the following before you can confirm and authorise pickup:</p>
          <ul className="list-disc pl-5 space-y-1">
            {!pr.quote_accepted && <li data-testid="m5-block-quote">Accept your quote</li>}
            {!pr.payment_completed && <li data-testid="m5-block-payment">Complete the Air12 payment</li>}
            {!pr.insurance_confirmed && <li data-testid="m5-block-insurance">Confirm insurance</li>}
          </ul>
        </div>
      ) : (
        <div className="space-y-4" data-testid="m5-ready">
          {preview ? <ConfirmReview review={preview.review} wording={preview.acknowledgement_wording} /> :
            <div className="text-sm text-[hsl(var(--muted))]" data-testid="m5-loading-review">Loading final review…</div>}
          <button className="btn-primary w-full" disabled={busy || !preview} onClick={confirm} data-testid="m5-confirm-authorise-pickup">
            {busy ? "Recording confirmation…" : "Confirm Shipment and Authorise Pickup"}
          </button>
        </div>
      )}
    </Panel>
  );
}

function ConfirmReview({ review, wording }) {
  const g = review.goods, c = review.commercials, p = review.parties, r = review.route;
  const Row = ({ k, v }) => (<div className="flex justify-between gap-4 py-0.5"><span className="text-[hsl(var(--muted))]">{k}</span><span className="text-right">{v ?? "—"}</span></div>);
  return (
    <div className="text-sm space-y-3" data-testid="m5-review">
      <div className="border rounded-sm p-3 space-y-1">
        <Row k="Sender" v={p.sender_company_legal_name} />
        <Row k="Receiver" v={p.receiver_name} />
        <Row k="Route" v={`${r.source_airport} → ${r.destination_airport}`} />
        <Row k="Goods" v={g.goods_description} />
        <Row k="Packages" v={g.package_count} />
        <Row k="Declared value" v={g.declared_invoice_value} />
        <Row k="Quote" v={`${c.quote_id} v${c.quote_version}`} />
        <Row k="Chargeable weight" v={`${c.chargeable_weight_kg} kg`} />
        <Row k="GST" v={c.gst_amount} />
        <Row k="Total payable" v={c.total_payable} />
        <Row k="PAN / GSTIN" v={`${p.sender_pan} / ${p.sender_gstin_selected || "No GSTIN"}`} />
      </div>
      <p className="text-[12px] text-[hsl(var(--muted))] border-t pt-2" data-testid="m5-acknowledgement">{wording}</p>
    </div>
  );
}



export function SenderAuthorityLetterPanel({ d, reload }) {
  const s = d.shipment;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadState = async () => {
    try {
      const { data } = await api.get(`/booking/${s.id}/authority-letter/state`);
      setState(data);
    } catch (e) { setState(null); } finally { setLoading(false); }
  };
  useEffect(() => { loadState(); }, [s.id]); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/booking/${s.id}/authority-letter/generate`);
      toast.success("Authority letter generated");
      await loadState(); reload && reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && detail.message) || friendlyError(e) || "Could not generate the letter. Please try again.");
      await loadState();
    } finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try { await api.post(`/booking/${s.id}/authority-letter/retry`); toast.success("Letter generation retried"); await loadState(); reload && reload(); }
    catch (e) { toast.error(friendlyError(e) || "Retry failed. Please try again."); }
    finally { setBusy(false); }
  };

  const download = async (letterId) => {
    try {
      const q = letterId ? `?letter_id=${letterId}` : "";
      const res = await api.get(`/booking/${s.id}/authority-letter/document${q}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open the document. Please try again."); }
  };

  if (loading || !state) return null;

  // Source-side operational actors (current Source CTOP / Captain / Pickup Rider): STATUS-ONLY.
  if (state.status_only) {
    return (
      <Panel icon={<Article size={16} />} title="Sender authority letter">
        <div className="text-sm" data-testid="m6-status-only">{state.label}</div>
        {state.generated_at && <div className="overline text-[10px]">Generated {fmtDT(state.generated_at)}</div>}
      </Panel>
    );
  }

  const { ready, m5_completed, source_captain_assigned, template_available, cancelled,
          is_owner, can_generate, can_retry, letter } = state;
  const title = "Sender Authority Letter for Cargo Booking and Handover";
  const hasCurrent = letter && letter.exists && letter.current;
  const stored = hasCurrent && letter.pdf_status === "STORED" && letter.download_available;
  const failed = hasCurrent && letter.pdf_status === "FAILED";

  const Instructions = () => (
    <div className="text-sm space-y-1 border-t pt-2 mt-2" data-testid="m6-instructions">
      <p className="text-[hsl(var(--muted))]">Handle this as a physical original:</p>
      <ol className="list-decimal pl-5 space-y-0.5">
        <li>Print the letter</li>
        <li>Wet-sign it</li>
        <li>Apply your company stamp where applicable</li>
        <li>Hand the original to the Pickup Rider</li>
      </ol>
    </div>
  );

  // Non-owner privileged (SUPER_ADMIN / source CTRL): download + retry-when-failed only.
  if (!is_owner) {
    if (!hasCurrent) return null;
    return (
      <Panel icon={<Article size={16} />} title="Sender authority letter">
        {stored && <button className="btn-ghost text-sm" onClick={() => download()} data-testid="m6-admin-download">Download authority letter (v{letter.letter_version})</button>}
        {failed && can_retry && (
          <div className="space-y-2">
            <p className="text-sm text-[hsl(var(--muted))]" data-testid="m6-admin-failed">The letter could not be prepared.</p>
            <button className="btn-primary" disabled={busy} onClick={retry} data-testid="m6-admin-retry">Retry letter generation</button>
          </div>
        )}
      </Panel>
    );
  }

  // Owning Sender
  return (
    <Panel icon={<Article size={16} />} title={title}>
      {cancelled ? (
        <div className="space-y-2 text-sm" data-testid="m6-cancelled">
          <p className="text-[hsl(var(--muted))]">This shipment has been cancelled.</p>
          {stored && <button className="btn-ghost text-sm" onClick={() => download()} data-testid="m6-download-historical">Download letter (historical)</button>}
        </div>
      ) : stored ? (
        <div className="space-y-3" data-testid="m6-generated">
          <div className="flex items-center gap-2 text-sm"><CheckCircle size={18} weight="fill" className="text-green-600" /> Letter ready · v{letter.letter_version}</div>
          {letter.generated_at && <div className="overline text-[10px]">Generated {fmtDT(letter.generated_at)}</div>}
          {letter.source_captain_full_name && <div className="text-sm text-[hsl(var(--muted))]">Source Captain: {letter.source_captain_full_name}</div>}
          <button className="btn-ghost text-sm" onClick={() => download()} data-testid="m6-download">Download authority letter</button>
          <Instructions />
        </div>
      ) : failed ? (
        <div className="space-y-2 text-sm" data-testid="m6-failed">
          <div className="flex items-center gap-2"><XCircle size={18} className="text-red-600" /> The letter could not be prepared.</div>
          <p className="text-[hsl(var(--muted))]">You don’t need to confirm your shipment again.</p>
          <button className="btn-primary" disabled={busy} onClick={retry} data-testid="m6-retry">Retry</button>
        </div>
      ) : hasCurrent ? (
        <div className="text-sm space-y-1" data-testid="m6-preparing">
          <div className="flex items-center gap-2"><InfoIcon size={18} className="text-[hsl(var(--muted))]" /> Preparing your authority letter…</div>
        </div>
      ) : !ready ? (
        <div className="space-y-2 text-sm" data-testid="m6-blocked">
          <p className="text-[hsl(var(--muted))]">This letter becomes available once:</p>
          <ul className="list-disc pl-5 space-y-1">
            {!m5_completed && <li data-testid="m6-block-m5">You complete Confirm Shipment and Authorise Pickup</li>}
            {!source_captain_assigned && <li data-testid="m6-block-captain">Air12 assigns your Source Captain</li>}
            {!template_available && <li data-testid="m6-block-template">The letter template is temporarily unavailable</li>}
          </ul>
        </div>
      ) : (
        <div className="space-y-3" data-testid="m6-eligible">
          <p className="text-sm text-[hsl(var(--muted))]">This letter authorises Air12 to book and hand over your cargo. It must be printed, wet-signed and company-stamped, then handed to the Pickup Rider as a physical original.</p>
          <button className="btn-primary w-full" disabled={busy || !can_generate} onClick={generate} data-testid="m6-generate">
            {busy ? "Preparing letter…" : "Generate authority letter"}
          </button>
        </div>
      )}
    </Panel>
  );
}


// M7 — physical Authority-Letter custody. Sender sees progress; the exact current Source Captain gets
// a receipt action; Source CTOP/CTRL/SUPER_ADMIN see status-only. No PDF, no upload/photo/signature.
export function AuthorityLetterCustodyPanel({ d, reload }) {
  const s = d.shipment;
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/booking/${s.id}/authority-letter/custody/state`);
      setState(data);
    } catch (e) { setState(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [s.id]); // eslint-disable-line

  const receive = async () => {
    setBusy(true);
    try {
      await api.post(`/booking/${s.id}/authority-letter/custody/receive`);
      toast.success("Original Authority Letter receipt recorded");
      await load(); reload && reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && detail.message) || friendlyError(e) || "Could not record receipt.");
      await load();
    } finally { setBusy(false); }
  };

  if (loading || !state) return null;

  return (
    <Panel icon={<ClipboardText size={16} />} title="Authority letter — physical custody">
      <div className="space-y-2 text-sm" data-testid="m7-custody-panel">
        <div className="flex items-center gap-2">
          {state.rider_collection_completed
            ? <CheckCircle size={16} weight="fill" className="text-green-600" />
            : <InfoIcon size={16} className="text-[hsl(var(--muted))]" />}
          <span data-testid="m7-rider-status">
            {state.rider_collection_completed
              ? `Original collected by Pickup Rider${state.rider_collected_at ? ` · ${fmtDT(state.rider_collected_at)}` : ""}`
              : "Awaiting Pickup Rider collection"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {state.captain_receipt_completed
            ? <CheckCircle size={16} weight="fill" className="text-green-600" />
            : <InfoIcon size={16} className="text-[hsl(var(--muted))]" />}
          <span data-testid="m7-captain-status">
            {state.captain_receipt_completed
              ? `Original received by Source Captain${state.captain_received_at ? ` · ${fmtDT(state.captain_received_at)}` : ""}`
              : "Awaiting Source Captain receipt"}
          </span>
        </div>
        {state.replacement_required && (
          <div className="text-[hsl(var(--muted))]" data-testid="m7-replacement-required">
            A replacement Authority Letter is required before custody can continue.
          </div>
        )}
        {state.can_receive && (
          <button className="btn-primary" disabled={busy} onClick={receive} data-testid="m7-captain-receive">
            {busy ? "Recording…" : "Confirm Original Authority Letter Received"}
          </button>
        )}
      </div>
    </Panel>
  );
}
